import api from './api';

class UserService {
  // ==================== UTILISATEURS ====================
  
  async getAllUsers(params = {}) {
    const response = await api.get('/users', { params });
    return response.data;
  }

  async getUser(id) {
    const response = await api.get(`/users/${id}`);
    return response.data;
  }

  async createUser(userData) {
    const response = await api.post('/users', userData);
    return response.data;
  }

  async updateUser(id, userData) {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  }

  async deleteUser(id) {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  }

  async changeUserPassword(id, passwordData) {
    const response = await api.post(`/users/${id}/change-password`, passwordData);
    return response.data;
  }

  async activateUser(id) {
    const response = await api.post(`/users/${id}/activate`);
    return response.data;
  }

  async deactivateUser(id) {
    const response = await api.post(`/users/${id}/deactivate`);
    return response.data;
  }

  // ==================== PROFIL UTILISATEUR ====================
  
  async getProfile() {
    const response = await api.get('/users/profile');
    return response.data;
  }

  async updateProfile(userData) {
    const response = await api.put('/users/profile', userData);
    return response.data;
  }

  async changePassword(passwordData) {
    const response = await api.post('/users/profile/change-password', passwordData);
    return response.data;
  }

  // ==================== STATISTIQUES ====================
  
  async getUserStatistics() {
    const response = await api.get('/users/statistics');
    return response.data;
  }

  // ==================== RECHERCHE ====================
  
  async searchUsers(query) {
    const response = await api.get('/users/search', { params: { q: query } });
    return response.data;
  }

  // ==================== RÔLES ====================
  
  async getAllRoles() {
    const response = await api.get('/users/roles');
    return response.data;
  }

  async getRole(id) {
    const response = await api.get(`/users/roles/${id}`);
    return response.data;
  }

  async createRole(roleData) {
    const response = await api.post('/users/roles', roleData);
    return response.data;
  }

  async updateRole(id, roleData) {
    const response = await api.put(`/users/roles/${id}`, roleData);
    return response.data;
  }

  async deleteRole(id) {
    const response = await api.delete(`/users/roles/${id}`);
    return response.data;
  }

  async assignPermissionsToRole(roleId, permissions) {
    const response = await api.post(`/users/roles/${roleId}/permissions`, { permissions });
    return response.data;
  }

  // ==================== PERMISSIONS ====================
  
  async getAllPermissions() {
    const response = await api.get('/users/permissions');
    return response.data;
  }

  async getPermission(id) {
    const response = await api.get(`/users/permissions/${id}`);
    return response.data;
  }

  // ==================== ACTIVITÉS UTILISATEUR ====================
  
  async getUserActivity(userId, params = {}) {
    const response = await api.get(`/users/${userId}/activity`, { params });
    return response.data;
  }

  async getMyActivity(params = {}) {
    const response = await api.get('/users/profile/activity', { params });
    return response.data;
  }

  // ==================== SESSIONS ====================
  
  async getUserSessions(userId) {
    const response = await api.get(`/users/${userId}/sessions`);
    return response.data;
  }

  async getMySessions() {
    const response = await api.get('/users/profile/sessions');
    return response.data;
  }

  async terminateSession(sessionId) {
    const response = await api.delete(`/users/sessions/${sessionId}`);
    return response.data;
  }

  async terminateAllSessions(userId) {
    const response = await api.delete(`/users/${userId}/sessions`);
    return response.data;
  }

  async terminateMyOtherSessions() {
    const response = await api.delete('/users/profile/sessions');
    return response.data;
  }

  // ==================== ÉQUIPE / AGENCE ====================
  
  async getAgencyUsers(agencyId, params = {}) {
    const response = await api.get(`/agencies/${agencyId}/users`, { params });
    return response.data;
  }

  async assignUserToAgency(userId, agencyId) {
    const response = await api.put(`/users/${userId}/agency`, { agency_id: agencyId });
    return response.data;
  }

  async assignUserToRole(userId, roleId) {
    const response = await api.put(`/users/${userId}/role`, { role_id: roleId });
    return response.data;
  }

  // ==================== IMPORT/EXPORT ====================
  
  async exportUsers(format = 'csv', params = {}) {
    const response = await api.get('/users/export', {
      params: { format, ...params },
      responseType: 'blob'
    });
    return response.data;
  }

  async importUsers(file, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    
    if (options.agency_id) {
      formData.append('agency_id', options.agency_id);
    }
    
    if (options.role_id) {
      formData.append('role_id', options.role_id);
    }
    
    const response = await api.post('/users/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  // ==================== NOTIFICATIONS UTILISATEUR ====================
  
  async getUserNotifications(userId, params = {}) {
    const response = await api.get(`/users/${userId}/notifications`, { params });
    return response.data;
  }

  async getMyNotifications(params = {}) {
    const response = await api.get('/users/profile/notifications', { params });
    return response.data;
  }

  async markNotificationAsRead(notificationId) {
    const response = await api.put(`/users/notifications/${notificationId}/read`);
    return response.data;
  }

  async markAllNotificationsAsRead() {
    const response = await api.put('/users/notifications/read-all');
    return response.data;
  }

  // ==================== PRÉFÉRENCES UTILISATEUR ====================
  
  async getUserPreferences(userId) {
    const response = await api.get(`/users/${userId}/preferences`);
    return response.data;
  }

  async getMyPreferences() {
    const response = await api.get('/users/profile/preferences');
    return response.data;
  }

  async updateUserPreferences(userId, preferences) {
    const response = await api.put(`/users/${userId}/preferences`, preferences);
    return response.data;
  }

  async updateMyPreferences(preferences) {
    const response = await api.put('/users/profile/preferences', preferences);
    return response.data;
  }

  // ==================== AUDIT ====================
  
  async getUserAuditLog(userId, params = {}) {
    const response = await api.get(`/users/${userId}/audit-log`, { params });
    return response.data;
  }

  // ==================== RÉINITIALISATION MOT DE PASSE ====================
  
  async requestPasswordReset(email) {
    const response = await api.post('/users/password-reset/request', { email });
    return response.data;
  }

  async resetPassword(token, newPassword) {
    const response = await api.post('/users/password-reset/reset', {
      token,
      new_password: newPassword
    });
    return response.data;
  }

  async validateResetToken(token) {
    const response = await api.post('/users/password-reset/validate', { token });
    return response.data;
  }

  // ==================== AVATAR/PHOTO ====================
  
  async uploadAvatar(userId, imageFile) {
    const formData = new FormData();
    formData.append('avatar', imageFile);
    
    const response = await api.post(`/users/${userId}/avatar`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  async uploadMyAvatar(imageFile) {
    const formData = new FormData();
    formData.append('avatar', imageFile);
    
    const response = await api.post('/users/profile/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  async deleteAvatar(userId) {
    const response = await api.delete(`/users/${userId}/avatar`);
    return response.data;
  }

  async deleteMyAvatar() {
    const response = await api.delete('/users/profile/avatar');
    return response.data;
  }

  // ==================== SIGNATURE ====================
  
  async uploadSignature(userId, signatureFile) {
    const formData = new FormData();
    formData.append('signature', signatureFile);
    
    const response = await api.post(`/users/${userId}/signature`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  async getSignature(userId) {
    const response = await api.get(`/users/${userId}/signature`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // ==================== MÉTRIQUES DE PERFORMANCE ====================
  
  async getUserPerformanceMetrics(userId, params = {}) {
    const response = await api.get(`/users/${userId}/performance`, { params });
    return response.data;
  }

  async getMyPerformanceMetrics(params = {}) {
    const response = await api.get('/users/profile/performance', { params });
    return response.data;
  }

  // ==================== DASHBOARD UTILISATEUR ====================
  
  async getUserDashboard(userId) {
    const response = await api.get(`/users/${userId}/dashboard`);
    return response.data;
  }

  async getMyDashboard() {
    const response = await api.get('/users/profile/dashboard');
    return response.data;
  }

  // ==================== FORMATIONS ET COMPÉTENCES ====================
  
  async getUserTrainings(userId) {
    const response = await api.get(`/users/${userId}/trainings`);
    return response.data;
  }

  async addUserTraining(userId, trainingData) {
    const response = await api.post(`/users/${userId}/trainings`, trainingData);
    return response.data;
  }

  async updateUserTraining(trainingId, trainingData) {
    const response = await api.put(`/users/trainings/${trainingId}`, trainingData);
    return response.data;
  }

  async deleteUserTraining(trainingId) {
    const response = await api.delete(`/users/trainings/${trainingId}`);
    return response.data;
  }

  // ==================== DOCUMENTS UTILISATEUR ====================
  
  async getUserDocuments(userId, params = {}) {
    const response = await api.get(`/users/${userId}/documents`, { params });
    return response.data;
  }

  async uploadUserDocument(userId, documentData) {
    const formData = new FormData();
    formData.append('file', documentData.file);
    formData.append('type', documentData.type);
    formData.append('description', documentData.description || '');
    
    const response = await api.post(`/users/${userId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  async deleteUserDocument(documentId) {
    const response = await api.delete(`/users/documents/${documentId}`);
    return response.data;
  }

  // ==================== CONTRATS ET EMPLOI ====================
  
  async getUserContract(userId) {
    const response = await api.get(`/users/${userId}/contract`);
    return response.data;
  }

  async updateUserContract(userId, contractData) {
    const response = await api.put(`/users/${userId}/contract`, contractData);
    return response.data;
  }

  async getUserEmploymentHistory(userId) {
    const response = await api.get(`/users/${userId}/employment-history`);
    return response.data;
  }

  async addEmploymentRecord(userId, recordData) {
    const response = await api.post(`/users/${userId}/employment-history`, recordData);
    return response.data;
  }

  // ==================== CONGÉS ET ABSENCES ====================
  
  async getUserLeaves(userId, params = {}) {
    const response = await api.get(`/users/${userId}/leaves`, { params });
    return response.data;
  }

  async requestLeave(leaveData) {
    const response = await api.post('/users/leaves', leaveData);
    return response.data;
  }

  async updateLeave(leaveId, leaveData) {
    const response = await api.put(`/users/leaves/${leaveId}`, leaveData);
    return response.data;
  }

  async deleteLeave(leaveId) {
    const response = await api.delete(`/users/leaves/${leaveId}`);
    return response.data;
  }

  // ==================== TÂCHES ET ASSIGNATIONS ====================
  
  async getUserTasks(userId, params = {}) {
    const response = await api.get(`/users/${userId}/tasks`, { params });
    return response.data;
  }

  async assignTask(taskData) {
    const response = await api.post('/users/tasks', taskData);
    return response.data;
  }

  async updateTask(taskId, taskData) {
    const response = await api.put(`/users/tasks/${taskId}`, taskData);
    return response.data;
  }

  // ==================== API KEYS ====================
  
  async getUserApiKeys(userId) {
    const response = await api.get(`/users/${userId}/api-keys`);
    return response.data;
  }

  async createApiKey(userId, keyData) {
    const response = await api.post(`/users/${userId}/api-keys`, keyData);
    return response.data;
  }

  async revokeApiKey(keyId) {
    const response = await api.delete(`/users/api-keys/${keyId}`);
    return response.data;
  }

  // ==================== DEUX FACTEURS D'AUTHENTIFICATION ====================
  
  async setupTwoFactor() {
    const response = await api.post('/users/profile/two-factor/setup');
    return response.data;
  }

  async verifyTwoFactor(code) {
    const response = await api.post('/users/profile/two-factor/verify', { code });
    return response.data;
  }

  async disableTwoFactor() {
    const response = await api.post('/users/profile/two-factor/disable');
    return response.data;
  }

  // ==================== UTILITAIRES ====================
  
  async checkUsernameAvailability(username) {
    const response = await api.get('/users/check-username', {
      params: { username }
    });
    return response.data;
  }

  async checkEmailAvailability(email) {
    const response = await api.get('/users/check-email', {
      params: { email }
    });
    return response.data;
  }

  async bulkUpdateUsers(userIds, updateData) {
    const response = await api.put('/users/bulk-update', {
      user_ids: userIds,
      ...updateData
    });
    return response.data;
  }

  async bulkDeleteUsers(userIds) {
    const response = await api.delete('/users/bulk-delete', {
      data: { user_ids: userIds }
    });
    return response.data;
  }

  // ==================== LOGS D'ACTIVITÉ ====================
  
  async getLoginHistory(userId, params = {}) {
    const response = await api.get(`/users/${userId}/login-history`, { params });
    return response.data;
  }

  async getMyLoginHistory(params = {}) {
    const response = await api.get('/users/profile/login-history', { params });
    return response.data;
  }

  // ==================== BADGES ET RÉCOMPENSES ====================
  
  async getUserBadges(userId) {
    const response = await api.get(`/users/${userId}/badges`);
    return response.data;
  }

  async awardBadge(userId, badgeData) {
    const response = await api.post(`/users/${userId}/badges`, badgeData);
    return response.data;
  }

  // ==================== INTÉGRATIONS ====================
  
  async getUserIntegrations(userId) {
    const response = await api.get(`/users/${userId}/integrations`);
    return response.data;
  }

  async connectIntegration(userId, integrationData) {
    const response = await api.post(`/users/${userId}/integrations`, integrationData);
    return response.data;
  }

  async disconnectIntegration(integrationId) {
    const response = await api.delete(`/users/integrations/${integrationId}`);
    return response.data;
  }

  // ==================== RAPPORTS PERSONNALISÉS ====================
  
  async generateUserReport(userId, reportData) {
    const response = await api.post(`/users/${userId}/reports`, reportData);
    return response.data;
  }

  async getReportTemplates() {
    const response = await api.get('/users/reports/templates');
    return response.data;
  }

  // ==================== BACKUP PROFIL ====================
  
  async backupProfileData() {
    const response = await api.post('/users/profile/backup');
    return response.data;
  }

  async restoreProfileData(backupData) {
    const response = await api.post('/users/profile/restore', backupData);
    return response.data;
  }

  // ==================== SUPPORT ET TICKETS ====================
  
  async createSupportTicket(ticketData) {
    const response = await api.post('/users/support-tickets', ticketData);
    return response.data;
  }

  async getMySupportTickets(params = {}) {
    const response = await api.get('/users/profile/support-tickets', { params });
    return response.data;
  }

  // ==================== STATISTIQUES AVANCÉES ====================
  
  async getUsageStatistics(userId, params = {}) {
    const response = await api.get(`/users/${userId}/usage-statistics`, { params });
    return response.data;
  }

  async getContributionMetrics(userId) {
    const response = await api.get(`/users/${userId}/contribution-metrics`);
    return response.data;
  }
}

export default new UserService();